import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { api } from "@/lib/api";

const COUNTRY_FLAGS = {
  canada: "🇨🇦", uk: "🇬🇧", "united kingdom": "🇬🇧", europe: "🇪🇺",
  usa: "🇺🇸", australia: "🇦🇺", "new zealand": "🇳🇿", germany: "🇩🇪", ireland: "🇮🇪",
};
const flagFor = (country) => COUNTRY_FLAGS[(country || "").toLowerCase()] || "🌍";

const ROTATE_MS = 7000;
const VISIBLE = 3;

export const ReviewsCarousel = () => {
  const [reviews, setReviews] = useState([]);
  const [start, setStart] = useState(0);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (reviews.length <= VISIBLE) return;
    const t = setInterval(() => setStart((s) => (s + VISIBLE) % reviews.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [reviews.length]);

  const visible = useMemo(() => {
    if (!reviews.length) return [];
    return Array.from(
      { length: Math.min(VISIBLE, reviews.length) },
      (_, i) => reviews[(start + i) % reviews.length]
    );
  }, [reviews, start]);

  if (!visible.length) return null;

  return (
    <div data-testid="reviews-carousel">
      <AnimatePresence mode="wait">
        <motion.div
          key={start}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="grid md:grid-cols-3 gap-6"
        >
          {visible.map((r) => (
            <div
              key={r.id}
              data-testid={`review-card-${r.id}`}
              className="card-hover rounded-3xl bg-white p-8 border border-brand-navy/10 relative flex flex-col"
            >
              <Quote className="absolute top-6 right-6 w-8 h-8 text-brand-red/15" />
              <div className="flex gap-1 text-brand-gold mb-4">
                {[...Array(r.rating || 5)].map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}
              </div>
              <p className="text-brand-navy/80 leading-relaxed flex-1">&quot;{r.quote}&quot;</p>
              <div className="mt-6 pt-6 border-t border-brand-navy/10 flex items-center gap-3">
                {r.image ? (
                  <img
                    src={r.image}
                    alt={r.name}
                    loading="lazy"
                    className="w-12 h-12 rounded-full object-cover object-top ring-2 ring-brand-red/20 shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-brand-red/10 text-brand-red grid place-items-center font-heading font-black shrink-0">
                    {(r.name || "?").charAt(0)}
                  </div>
                )}
                <div>
                  <div className="font-heading font-bold text-brand-navy">{r.name}</div>
                  <div className="text-sm text-brand-navy/50">{flagFor(r.country)} Now in {r.country}</div>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </AnimatePresence>

      {reviews.length > VISIBLE && (
        <div className="mt-8 flex justify-center gap-2" data-testid="reviews-dots">
          {reviews.map((r, i) => (
            <button
              key={r.id}
              onClick={() => setStart(i)}
              aria-label={`Show reviews from ${r.name}`}
              data-testid={`reviews-dot-${i}`}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === start ? "w-8 bg-brand-red" : "w-2 bg-brand-navy/20 hover:bg-brand-navy/40"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
